import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { financialReportsAPI, type TrialBalance } from '../../services/api';

interface TrialBalanceReportProps {
  siteId: string;
  asOfDate: string;
}

const TrialBalanceReport: React.FC<TrialBalanceReportProps> = ({ siteId, asOfDate }) => {
  const { data, isLoading, error } = useQuery(
    ['trial-balance', siteId, asOfDate],
    () => financialReportsAPI.getTrialBalance(siteId, { asOfDate })
  );

  const trialBalanceData = data?.data;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading trial balance</p>
      </div>
    );
  }

  if (!trialBalanceData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for the selected date</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Trial Balance</h3>
          <p className="text-sm text-gray-500">As of {new Date(asOfDate).toLocaleDateString()}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          trialBalanceData.isBalanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {trialBalanceData.isBalanced ? 'Balanced' : 'Out of Balance'}
        </div>
      </div>

      {/* Trial Balance Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Debit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trialBalanceData.accounts.map((account, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {account.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {account.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100">
                      {account.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {account.debit > 0 ? formatCurrency(account.debit) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {account.credit > 0 ? formatCurrency(account.credit) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={3} className="px-6 py-4 text-sm font-semibold text-gray-900">
                  Totals
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                  {formatCurrency(trialBalanceData.totalDebits)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                  {formatCurrency(trialBalanceData.totalCredits)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Balance Verification */}
      <div className={`p-4 rounded-lg ${trialBalanceData.isBalanced ? 'bg-green-50' : 'bg-red-50'}`}>
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-900">Balance Verification</span>
          <div className="text-right">
            <div className="text-sm text-gray-600">
              Difference: {formatCurrency(Math.abs(trialBalanceData.totalDebits - trialBalanceData.totalCredits))}
            </div>
            <div className={`text-sm font-medium ${
              trialBalanceData.isBalanced ? 'text-green-600' : 'text-red-600'
            }`}>
              {trialBalanceData.isBalanced ? 'Trial balance is balanced' : 'Trial balance is out of balance'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBalanceReport;
